// SharedHeader.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  memo, // Import memo
} from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuIcon,
  CreditCardIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "./Icons";
import LanguageSelector from "../components/LanguageSelector";
import { LanguageContext } from "../contexts/LanguageContext";
import { UserContext } from "../contexts/UserContext";

// Memoize SharedHeader to prevent unnecessary re-renders
const SharedHeader = memo(({ // Wrap the component in memo
  setCurrentPage,
  isSidebarOpen,
  setIsSidebarOpen = () => {},
}) => {
  const { language } = useContext(LanguageContext);
  const { userData, userLoading, userError, refreshUserData } = useContext(UserContext);
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const profileDropdownRef = useRef(null);

  // Refresh user data every 1 second (1000 ms)
  useEffect(() => {
    // Call immediately once on mount
    refreshUserData();

    // Set interval to refresh every second
    const intervalId = setInterval(() => {
      refreshUserData();
    }, 1000);

    // Clear interval on unmount
    return () => clearInterval(intervalId);
  }, [refreshUserData]);

  const translations = {
    en: {
      player: "Player",
      guest: "Guest",
      loading: "Loading...",
      dashboard: "Home",
      contact: "Contact",
      leaderboard: "Leaderboard",
      howToPlay: "How To Play",
      referralIncome: "Referral Income",
      wallet: "Wallet",
      profile: "Profile",
      history: "History",
      logout: "Log out",
      bingoGame: "Bingo",
      birr: "Birr",
    },
    am: {
      player: "ተጫዋች",
      guest: "እንግዳ",
      loading: "በመጫን ላይ...",
      dashboard: "መነሻ",
      contact: "አግኝነት",
      leaderboard: "የተወዳዳሪዎች ዝርዝር",
      howToPlay: "እንዴት መጫወት እንደሚቻል",
      referralIncome: "የሪፈራል ገቢ",
      wallet: "የገንዘብ ቦርሳ",
      profile: "መገለጫ",
      history: "ታሪክ",
      logout: "ውጣ",
      bingoGame: "ቢንጎ",
      birr: "ብር",
    },
    or: {
      player: "Taphataa",
      guest: "Daawwitii",
      loading: "Fe'aa jira...",
      dashboard: "Mana",
      contact: "Quunnamtii",
      leaderboard: "Dorgommii",
      howToPlay: "Akkaataa Taphachuu",
      referralIncome: "Galii Yaamicha",
      wallet: "Arfii",
      profile: "Ibsaa",
      history: "Seenaa",
      logout: "Bahi",
      bingoGame: "Bingo",
      birr: "Birrii",
    },
    ti: {
      player: "ተጫዋቲ",
      guest: "ጎብኚ",
      loading: "እዚ ዝተሳተፈ እዩ...",
      dashboard: "መነሻ",
      contact: "ኣገናኝታ",
      leaderboard: "ዝኾነ ኣሸናፊ ዝርዝር",
      howToPlay: "ከመይ ክጫወት እንተሃልዎ",
      referralIncome: "ገበሬ ገንዘብ",
      wallet: "ቦርሳ",
      profile: "መግለጺ",
      history: "ታሪኽ",
      logout: "ኣውጺእ",
      bingoGame: "ቢንጎ",
      birr: "ብር",
    },
    wo: {
      player: "Olee",
      guest: "Bariisaa",
      loading: "Fe'aa jiruu...",
      dashboard: "Mana",
      contact: "Quunnamtii",
      leaderboard: "Tarree Tapha Gaggeessaa",
      howToPlay: "Akka Taphtan",
      referralIncome: "Galii Walitti Fufiinsa",
      wallet: "Boorsee Maallaqaa",
      profile: "Seensa",
      history: "Seenaa",
      logout: "Bahi",
      bingoGame: "Bingo",
      birr: "Birrii",
    },
  };

  const t = useCallback(
    (key) => translations[language]?.[key] ?? translations.en[key],
    [language]
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(e.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsProfileDropdownOpen(false);
  };

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const toggleProfileDropdown = (e) => {
    e.stopPropagation();
    setIsProfileDropdownOpen((prev) => !prev);
  };

  if (userLoading) {
    // Optionally handle loading state
  }
  if (userError) {
    if (
      userError.message &&
      (userError.message.includes("401") || userError.message.includes("403"))
    ) {
      localStorage.removeItem("token");
      navigate("/login");
    }
  }

  return (
    <>
      {/* Mobile Header */}
      <header
        className={`w-full bg-[#2f3650] px-4 py-3 flex items-center justify-between fixed top-0 z-20 md:hidden ${
          isScrolled ? "shadow-lg" : "shadow-md"
        }`}
      >
        <button onClick={toggleSidebar} className="text-white">
          <MenuIcon />
        </button>
        <div className="flex items-center space-x-4">
          <div className="w-[160px] mr-3">
            <LanguageSelector isMobile />
          </div>
          {/* Modified Mobile Balance Link */}
          <button
            onClick={() => handleNavigation("/wallet")}
            className="flex items-center bg-[#3c4561] rounded-full px-3 py-1 cursor-pointer"
          >
            <CreditCardIcon className="text-yellow-400 mr-2" />
            <span className="text-white font-semibold text-sm">
              {userData.depositBalance !== undefined
                ? userData.depositBalance.toFixed(2)
                : "0.00"}{" "}
              {t("birr")}
            </span>
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <nav
        className={`hidden md:flex w-full bg-[#2f3650] p-4 items-center justify-between fixed top-0 z-50 ${
          isScrolled ? "shadow-lg" : "shadow-md"
        }`}
      >
        <div className="flex items-center space-x-2">
          <div
            className="flex items-center cursor-pointer"
            onClick={() => handleNavigation("/dashboard")}
          >
            <img
              src="/logo.png"
              alt="Logo"
              className="h-10 mr-2"
              onError={(e) => {
                e.target.src =
                  "https://placehold.co/40x40/F49C00/FFFFFF?text=B";
              }}
            />
            <span className="text-white text-xl font-bold">{t("bingoGame")}</span>
          </div>
          <ul className="flex space-x-6">
            {[
              "dashboard",
              "leaderboard",
              "howToPlay",
              "referralIncome",
            ].map((key) => (
              <li key={key}>
                <button
                  onClick={() =>
                    handleNavigation(
                      `/${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`
                    )
                  }
                  className="text-white hover:text-gray-300 font-medium"
                >
                  {t(key)}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center space-x-4">
          <div className="w-[160px] mr-3">
            <LanguageSelector />
          </div>
          {/* Modified Desktop Balance Link */}
          <button
            onClick={() => handleNavigation("/wallet")}
            className="flex items-center cursor-pointer"
          >
            <CreditCardIcon className="text-yellow-400 mr-2" />
            <span className="text-white font-semibold text-sm">
              {userData.depositBalance !== undefined
                ? userData.depositBalance.toFixed(2)
                : "0.00"}{" "}
              {t("birr")}
            </span>
          </button>
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={toggleProfileDropdown}
              className="text-white flex items-center font-medium"
            >
              <UserIcon className="mr-2" />
              {userData.fullName || t("guest")}
              {isProfileDropdownOpen ? (
                <ChevronUpIcon className="ml-1" />
              ) : (
                <ChevronDownIcon className="ml-1" />
              )}
            </button>
            {isProfileDropdownOpen && (
              <ul className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg py-1 z-50">
                {["wallet", "profile", "history"].map((key) => (
                  <li key={key}>
                    <button
                      onClick={() => handleNavigation(`/${key}`)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t(key)}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => {
                      localStorage.removeItem("token");
                      handleNavigation("/login");
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    {t("logout")}
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </nav>
    </>
  );
});

export default SharedHeader;